import React, { useEffect, useRef, useState } from 'react'
import {
  Type, Heading1, Heading2, Heading3, List, ListOrdered, CheckSquare,
  Quote, Code2, Minus, Table2, Paperclip, GripVertical, Trash2, Copy,
  ArrowUp, ArrowDown, UploadCloud,
} from 'lucide-react'
import { cn } from '../../utils/cn'
import { newBlock } from './useWorkspace'
import TableBlock from './TableBlock'
import AttachmentBlock from './AttachmentBlock'
import SelectionToolbar from './SelectionToolbar'

// ── Caret helpers (plain-text contentEditable) ───────────────────────────────

function caretOffset(el) {
  const sel = window.getSelection()
  if (!sel || sel.rangeCount === 0) return 0
  const range = sel.getRangeAt(0)
  const pre = range.cloneRange()
  pre.selectNodeContents(el)
  pre.setEnd(range.endContainer, range.endOffset)
  return pre.toString().length
}

function setCaret(el, offset) {
  if (el.tagName === 'TEXTAREA') {
    el.setSelectionRange(offset, offset)
    return
  }
  const sel = window.getSelection()
  const range = document.createRange()
  let remaining = offset
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT)
  let node = walker.nextNode()
  while (node) {
    if (remaining <= node.length) {
      range.setStart(node, remaining)
      range.collapse(true)
      sel.removeAllRanges()
      sel.addRange(range)
      return
    }
    remaining -= node.length
    node = walker.nextNode()
  }
  range.selectNodeContents(el)
  range.collapse(false)
  sel.removeAllRanges()
  sel.addRange(range)
}

// ── Rich-text (inline HTML) helpers ──────────────────────────────────────────

/** Plain-text length of an HTML string (for caret math after a merge). */
function plainLen(html) {
  const d = document.createElement('div')
  d.innerHTML = html || ''
  return (d.textContent || '').length
}

/** Split a contentEditable's HTML at the caret, preserving inline formatting. */
function splitHtmlAtCaret(el) {
  const sel = window.getSelection()
  if (!sel || sel.rangeCount === 0) return { before: el.innerHTML, after: '' }
  const range = sel.getRangeAt(0)
  const beforeR = range.cloneRange()
  beforeR.selectNodeContents(el); beforeR.setEnd(range.endContainer, range.endOffset)
  const afterR = range.cloneRange()
  afterR.selectNodeContents(el); afterR.setStart(range.endContainer, range.endOffset)
  const tmp = document.createElement('div')
  tmp.appendChild(beforeR.cloneContents()); const before = tmp.innerHTML
  tmp.innerHTML = ''; tmp.appendChild(afterR.cloneContents()); const after = tmp.innerHTML
  return { before, after }
}

// ── Markdown triggers ( "<prefix><space>" transforms the block ) ─────────────

const MD_TRIGGERS = {
  '#': 'h1', '##': 'h2', '###': 'h3',
  '-': 'bullet', '*': 'bullet', '1.': 'numbered',
  '[]': 'todo', '[ ]': 'todo', '>': 'quote',
}

const SLASH_ITEMS = [
  { key: 'p',        label: 'Text',        hint: 'Plain paragraph',          icon: Type },
  { key: 'h1',       label: 'Heading 1',   hint: '# + space',                icon: Heading1 },
  { key: 'h2',       label: 'Heading 2',   hint: '## + space',               icon: Heading2 },
  { key: 'h3',       label: 'Heading 3',   hint: '### + space',              icon: Heading3 },
  { key: 'bullet',   label: 'Bullet list', hint: '- + space',                icon: List },
  { key: 'numbered', label: 'Numbered list', hint: '1. + space',             icon: ListOrdered },
  { key: 'todo',     label: 'To-do',       hint: '[] + space',               icon: CheckSquare },
  { key: 'quote',    label: 'Quote',       hint: '> + space',                icon: Quote },
  { key: 'code',     label: 'Code',        hint: '``` ',                     icon: Code2 },
  { key: 'divider',  label: 'Divider',     hint: 'Horizontal rule',          icon: Minus },
  { key: 'table',    label: 'Table',       hint: 'Spreadsheet with formulas', icon: Table2 },
  { key: 'file',     label: 'File or image', hint: 'Upload — kept as original', icon: Paperclip },
]

const TEXT_TYPES = new Set(['p', 'h1', 'h2', 'h3', 'bullet', 'numbered', 'todo', 'quote'])

const TURN_INTO = SLASH_ITEMS.filter((i) => ['p', 'h1', 'h2', 'h3', 'bullet', 'numbered', 'todo', 'quote', 'code'].includes(i.key))

/**
 * Editor — title + block list. Text lives in a mutable note object; typing
 * never re-renders (caret-safe). Structural changes go through onStructure.
 */
export default function Editor({ note, rev, onText, onStructure, uploadAttachment }) {
  const rootRef = useRef(null)
  const blockEls = useRef(new Map())          // blockId → element
  const pendingFocus = useRef(null)           // { id, offset }
  const fileInputRef = useRef(null)
  const insertAfterRef = useRef(null)         // blockId to insert uploads after
  const [slash, setSlash] = useState(null)    // { blockId, query, idx }
  const [menuFor, setMenuFor] = useState(null) // blockId of open ⋮ menu
  const [dragging, setDragging] = useState(false)
  const [uploading, setUploading] = useState(0)
  const [fmtBar, setFmtBar] = useState(null)  // { top, left, blockId } | null
  const [fmtActive, setFmtActive] = useState({})

  const blocks = note.blocks

  // ── Inline formatting: floating ribbon on text selection ───────────────────

  // Persist a block's current innerHTML into the model (after a format command
  // or contentEditable mutation that React's onInput may not capture).
  const persistBlockHtml = (blockId) => {
    const el = blockEls.current.get(blockId)
    const b = blocks.find((x) => x.id === blockId)
    if (el && b && el.tagName !== 'TEXTAREA') { b.text = el.innerHTML; onText() }
  }

  const refreshFmtBar = () => {
    const sel = window.getSelection()
    if (!sel || sel.isCollapsed || sel.rangeCount === 0) { setFmtBar(null); return }
    const node = sel.anchorNode
    const host = node && (node.nodeType === 3 ? node.parentElement : node)
    const blockEl = host && host.closest && host.closest('.ws-block')
    if (!blockEl || !rootRef.current || !rootRef.current.contains(blockEl)) { setFmtBar(null); return }
    const rect = sel.getRangeAt(0).getBoundingClientRect()
    const rootRect = rootRef.current.getBoundingClientRect()
    if (!rect || (rect.width === 0 && rect.height === 0)) { setFmtBar(null); return }
    setFmtBar({
      top: rect.top - rootRect.top,
      left: rect.left - rootRect.left + rect.width / 2,
      blockId: blockEl.getAttribute('data-block-id'),
    })
    setFmtActive({
      bold: document.queryCommandState('bold'),
      italic: document.queryCommandState('italic'),
      underline: document.queryCommandState('underline'),
      strikeThrough: document.queryCommandState('strikeThrough'),
    })
  }

  useEffect(() => {
    const onSelChange = () => {
      // Only react when the selection is inside this editor.
      const sel = window.getSelection()
      const node = sel && sel.anchorNode
      const host = node && (node.nodeType === 3 ? node.parentElement : node)
      if (host && rootRef.current && rootRef.current.contains(host)) refreshFmtBar()
      else setFmtBar(null)
    }
    document.addEventListener('selectionchange', onSelChange)
    return () => document.removeEventListener('selectionchange', onSelChange)
  }, [])   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setFmtBar(null) }, [note.id, rev])

  const applyFmt = (type, value) => {
    if (!fmtBar) return
    const sel = window.getSelection()
    if (!sel || sel.rangeCount === 0) return
    document.execCommand('styleWithCSS', false, true)
    if (type === 'exec') {
      document.execCommand(value, false, null)
    } else if (type === 'color') {
      document.execCommand('foreColor', false, value)
    } else if (type === 'highlight') {
      document.execCommand('hiliteColor', false, value === 'transparent' ? 'rgba(0,0,0,0)' : value)
    } else if (type === 'font') {
      document.execCommand('fontName', false, value)
    } else if (type === 'size') {
      // execCommand fontSize is legacy 1–7; wrap the range in a styled span instead.
      const range = sel.getRangeAt(0)
      if (!range.collapsed) {
        const span = document.createElement('span')
        span.style.fontSize = value
        try { range.surroundContents(span) }
        catch { span.appendChild(range.extractContents()); range.insertNode(span) }
        const r = document.createRange(); r.selectNodeContents(span)
        sel.removeAllRanges(); sel.addRange(r)
      }
    } else if (type === 'clear') {
      document.execCommand('removeFormat', false, null)
      document.execCommand('unlink', false, null)
    }
    persistBlockHtml(fmtBar.blockId)
    refreshFmtBar()
  }

  // Apply queued focus after a structural re-render.
  useEffect(() => {
    const pf = pendingFocus.current
    if (!pf) return
    pendingFocus.current = null
    requestAnimationFrame(() => {
      const el = blockEls.current.get(pf.id)
      if (el) { el.focus(); setCaret(el, pf.offset ?? 0) }
    })
  }, [rev])

  useEffect(() => { setMenuFor(null); setSlash(null) }, [note.id])

  const idx = (id) => blocks.findIndex((b) => b.id === id)
  const focusBlock = (id, offset = 0) => { pendingFocus.current = { id, offset }; onStructure() }

  // ── Structural operations ──────────────────────────────────────────────────

  const insertBlockAfter = (afterId, block) => {
    const i = afterId == null ? blocks.length - 1 : idx(afterId)
    blocks.splice(i + 1, 0, block)
    return block
  }

  const removeBlock = (id) => {
    const i = idx(id)
    if (i === -1) return
    blocks.splice(i, 1)
    if (blocks.length === 0) blocks.push(newBlock('p'))
    const target = blocks[Math.max(0, i - 1)]
    pendingFocus.current = { id: target.id, offset: (target.text || '').length }
    onStructure()
  }

  const moveBlock = (id, dir) => {
    const i = idx(id)
    const j = i + dir
    if (i === -1 || j < 0 || j >= blocks.length) return
    const [b] = blocks.splice(i, 1)
    blocks.splice(j, 0, b)
    pendingFocus.current = { id, offset: 0 }
    onStructure()
  }

  const duplicateBlock = (id) => {
    const i = idx(id)
    const src = blocks[i]
    const copy = { ...JSON.parse(JSON.stringify(src)), id: newBlock('p').id }
    blocks.splice(i + 1, 0, copy)
    focusBlock(copy.id, 0)
  }

  const turnInto = (id, type) => {
    const i = idx(id)
    const b = blocks[i]
    if (type === 'table') {
      blocks[i] = { ...newBlock('table'), id: b.id }
    } else if (type === 'divider') {
      blocks[i] = { id: b.id, type: 'divider' }
      insertBlockAfter(b.id, newBlock('p'))
    } else {
      blocks[i] = { id: b.id, type, text: b.text || '', ...(type === 'todo' ? { checked: false } : {}) }
    }
    focusBlock(b.id, (blocks[i].text || '').length)
  }

  // ── Uploads ────────────────────────────────────────────────────────────────

  const handleFiles = async (fileList, afterId = null) => {
    const files = [...fileList]
    if (!files.length) return
    setUploading((u) => u + files.length)
    let anchor = afterId
    for (const file of files) {
      try {
        const att = await uploadAttachment(file)
        const block = newBlock('attachment', { attId: att.id })
        insertBlockAfter(anchor, block)
        anchor = block.id
        onStructure()
      } catch (err) {
        const msg = err.response?.data?.message || err.message || 'Upload failed'
        window.dispatchEvent(new CustomEvent('workspace-toast', { detail: `“${file.name}”: ${msg}` }))
      } finally {
        setUploading((u) => u - 1)
      }
    }
  }

  // ── Key handling for text blocks ───────────────────────────────────────────

  const handleKeyDown = (e, block) => {
    const el = e.currentTarget
    const text = el.textContent || ''
    const offset = caretOffset(el)
    const i = idx(block.id)

    // Slash menu navigation steals these keys while open
    if (slash?.blockId === block.id) {
      const items = filteredSlashItems(slash.query)
      if (e.key === 'ArrowDown') { e.preventDefault(); setSlash((s) => ({ ...s, idx: Math.min(items.length - 1, s.idx + 1) })); return }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setSlash((s) => ({ ...s, idx: Math.max(0, s.idx - 1) })); return }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault()
        if (items.length) applySlash(block, items[Math.min(slash.idx, items.length - 1)].key)
        return
      }
      if (e.key === 'Escape') { e.preventDefault(); setSlash(null); return }
    }

    if (e.altKey && e.key === 'ArrowUp')   { e.preventDefault(); moveBlock(block.id, -1); return }
    if (e.altKey && e.key === 'ArrowDown') { e.preventDefault(); moveBlock(block.id, +1); return }

    // Markdown trigger on space
    if (e.key === ' ' && TEXT_TYPES.has(block.type)) {
      const prefix = text.slice(0, offset)
      const t = MD_TRIGGERS[prefix]
      if (t && offset === prefix.length) {
        e.preventDefault()
        block.type = t
        block.text = text.slice(offset)
        if (t === 'todo') block.checked = false
        el.textContent = block.text
        focusBlock(block.id, 0)
        return
      }
    }

    // ``` → code block
    if (e.key === '`' && text === '``' && offset === 2) {
      e.preventDefault()
      block.type = 'code'
      block.text = ''
      focusBlock(block.id, 0)
      return
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      const before = text.slice(0, offset)
      const after = text.slice(offset)
      const isList = ['bullet', 'numbered', 'todo'].includes(block.type)
      if (isList && before === '' && after === '') {
        block.type = 'p'                       // empty list item → exit the list
        focusBlock(block.id, 0)
        return
      }
      // Split preserving inline formatting on both sides of the caret.
      const html = splitHtmlAtCaret(el)
      block.text = html.before
      el.innerHTML = html.before
      const nextType = isList ? block.type : 'p'
      const nb = newBlock(nextType, { text: html.after })
      insertBlockAfter(block.id, nb)
      focusBlock(nb.id, 0)
      return
    }

    if (e.key === 'Backspace' && offset === 0 && window.getSelection()?.isCollapsed) {
      if (block.type !== 'p') {
        e.preventDefault()
        block.type = 'p'
        delete block.checked
        focusBlock(block.id, 0)
        return
      }
      const prev = blocks[i - 1]
      if (!prev) return
      e.preventDefault()
      if (TEXT_TYPES.has(prev.type)) {
        const prevLen = plainLen(prev.text)
        prev.text = (prev.text || '') + (block.text || '')   // both inline HTML
        blocks.splice(i, 1)
        focusBlock(prev.id, prevLen)
      } else if (prev.type === 'divider') {
        blocks.splice(i - 1, 1)
        focusBlock(block.id, 0)
      }
      return
    }

    if (e.key === 'ArrowUp' && offset === 0) {
      const prev = [...blocks.slice(0, i)].reverse().find((b) => TEXT_TYPES.has(b.type) || b.type === 'code')
      if (prev) {
        e.preventDefault()
        const pel = blockEls.current.get(prev.id)
        if (pel) { pel.focus(); setCaret(pel, prev.type === 'code' ? (prev.text || '').length : plainLen(prev.text)) }
      }
      return
    }
    if (e.key === 'ArrowDown' && offset >= text.length) {
      const next = blocks.slice(i + 1).find((b) => TEXT_TYPES.has(b.type) || b.type === 'code')
      if (next) {
        e.preventDefault()
        const nel = blockEls.current.get(next.id)
        if (nel) { nel.focus(); setCaret(nel, 0) }
      }
    }
  }

  const handleInput = (e, block) => {
    block.text = e.currentTarget.innerHTML       // store inline HTML
    const text = e.currentTarget.textContent || ''
    onText()
    // Slash menu lifecycle (driven by the visible text)
    if (block.type === 'p' && text.startsWith('/')) {
      setSlash({ blockId: block.id, query: text.slice(1), idx: 0 })
    } else if (slash?.blockId === block.id) {
      setSlash(null)
    }
  }

  const handlePaste = (e, block) => {
    if (e.clipboardData.files?.length) {
      e.preventDefault()
      handleFiles(e.clipboardData.files, block.id)
      return
    }
    const textData = e.clipboardData.getData('text/plain')
    if (!textData) return
    e.preventDefault()
    const lines = textData.replace(/\r/g, '').split('\n')
    if (lines.length === 1) {
      // Plain-text insert (no HTML from the clipboard) — keeps the block safe
      // while preserving any existing inline formatting around the caret.
      document.execCommand('insertText', false, lines[0])
      block.text = e.currentTarget.innerHTML
      onText()
      return
    }
    // Multi-line paste → split into blocks
    const el = e.currentTarget
    const offset = caretOffset(el)
    const cur = el.textContent || ''
    block.text = cur.slice(0, offset) + lines[0]
    let anchor = block.id
    for (let li = 1; li < lines.length; li++) {
      const nb = newBlock('p', { text: lines[li] + (li === lines.length - 1 ? cur.slice(offset) : '') })
      insertBlockAfter(anchor, nb)
      anchor = nb.id
    }
    focusBlock(anchor, lines[lines.length - 1].length)
  }

  // ── Slash menu ─────────────────────────────────────────────────────────────

  const filteredSlashItems = (q) =>
    SLASH_ITEMS.filter((it) => it.label.toLowerCase().includes((q || '').toLowerCase()))

  const applySlash = (block, key) => {
    setSlash(null)
    block.text = ''
    const el = blockEls.current.get(block.id)
    if (el) el.textContent = ''
    if (key === 'file') {
      insertAfterRef.current = block.id
      fileInputRef.current?.click()
      onText()
      return
    }
    turnInto(block.id, key)
  }

  // ── Rendering ──────────────────────────────────────────────────────────────

  const registerEl = (id) => (el) => {
    if (el) {
      blockEls.current.set(id, el)
      const b = blocks.find((x) => x.id === id)
      if (!b) return
      if (el.tagName === 'TEXTAREA') {
        if (el.value !== (b.text || '')) el.value = b.text || ''
        el.style.height = 'auto'
        el.style.height = `${el.scrollHeight}px`
      } else if (el !== document.activeElement && el.innerHTML !== (b.text || '')) {
        // Render inline HTML; never overwrite while the user is typing in it.
        el.innerHTML = b.text || ''
      }
    } else {
      blockEls.current.delete(id)
    }
  }

  // numbered-list counters
  const numbers = {}
  let run = 0
  blocks.forEach((b) => {
    run = b.type === 'numbered' ? run + 1 : 0
    if (b.type === 'numbered') numbers[b.id] = run
  })

  const typeClass = {
    p:    'text-[15px] leading-7 text-ink-800',
    h1:   'text-[26px] leading-9 font-display font-semibold text-ink-900 tracking-tight',
    h2:   'text-xl leading-8 font-display font-semibold text-ink-900 tracking-tight',
    h3:   'text-base leading-7 font-semibold text-ink-900',
    bullet:   'text-[15px] leading-7 text-ink-800',
    numbered: 'text-[15px] leading-7 text-ink-800',
    todo:     'text-[15px] leading-7',
    quote: 'text-[15px] leading-7 text-ink-600 italic',
  }

  return (
    <div
      ref={rootRef}
      className="relative"
      onDragOver={(e) => { e.preventDefault(); setDragging(true) }}
      onDragLeave={(e) => { if (e.currentTarget === e.target) setDragging(false) }}
      onDrop={(e) => { e.preventDefault(); setDragging(false); if (e.dataTransfer.files?.length) handleFiles(e.dataTransfer.files) }}
    >
      <SelectionToolbar pos={fmtBar} active={fmtActive} onCommand={applyFmt} />
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => { handleFiles(e.target.files, insertAfterRef.current); e.target.value = '' }}
      />

      {dragging && (
        <div className="absolute inset-0 z-20 rounded-2xl border-2 border-dashed border-accent-400 bg-accent-50/80 backdrop-blur-[1px] flex items-center justify-center pointer-events-none">
          <div className="flex items-center gap-2 text-accent-700 font-semibold text-sm">
            <UploadCloud className="w-5 h-5" /> Drop to attach — files are kept exactly as uploaded
          </div>
        </div>
      )}

      {uploading > 0 && (
        <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-accent-50 px-3 py-1 text-xs font-medium text-accent-700">
          <span className="w-1.5 h-1.5 rounded-full bg-accent-500 animate-pulse" />
          Uploading {uploading} file{uploading > 1 ? 's' : ''}…
        </div>
      )}

      <div className="space-y-px pb-24">
        {blocks.map((block, i) => {
          const isText = TEXT_TYPES.has(block.type)
          return (
            <div key={block.id} className="group relative flex items-start gap-1 -ml-8 pl-8">
              {/* Gutter handle */}
              <button
                tabIndex={-1}
                onClick={() => setMenuFor(menuFor === block.id ? null : block.id)}
                className="absolute left-0 top-1 w-6 h-6 rounded-md flex items-center justify-center text-ink-300 opacity-0 group-hover:opacity-100 hover:bg-ink-100 hover:text-ink-500 transition-all"
                title="Block options"
              >
                <GripVertical className="w-3.5 h-3.5" />
              </button>

              {/* ⋮ menu */}
              {menuFor === block.id && (
                <>
                  <div className="fixed inset-0 z-30" onClick={() => setMenuFor(null)} />
                  <div className="absolute left-0 top-8 z-40 w-52 rounded-xl bg-white border border-ink-100 shadow-elev-3 py-1.5 animate-scale-in">
                    <div className="px-3 py-1 text-[10px] font-semibold text-ink-400 uppercase tracking-wider">Turn into</div>
                    <div className="grid grid-cols-3 gap-0.5 px-2 pb-1.5">
                      {TURN_INTO.map((it) => (
                        <button
                          key={it.key}
                          onClick={() => { setMenuFor(null); turnInto(block.id, it.key) }}
                          className={cn(
                            'flex flex-col items-center gap-0.5 rounded-lg px-1 py-1.5 text-[9px] text-ink-500 hover:bg-ink-50 hover:text-ink-800',
                            block.type === it.key && 'bg-accent-50 text-accent-700',
                          )}
                          title={it.label}
                        >
                          <it.icon className="w-3.5 h-3.5" />
                          {it.label.replace(' list', '').replace('Heading ', 'H')}
                        </button>
                      ))}
                    </div>
                    <div className="border-t border-ink-100 my-1" />
                    <button className="ws-menu-item" onClick={() => { setMenuFor(null); moveBlock(block.id, -1) }}>
                      <ArrowUp className="w-3.5 h-3.5" /> Move up <kbd className="ws-kbd ml-auto">⌥↑</kbd>
                    </button>
                    <button className="ws-menu-item" onClick={() => { setMenuFor(null); moveBlock(block.id, +1) }}>
                      <ArrowDown className="w-3.5 h-3.5" /> Move down <kbd className="ws-kbd ml-auto">⌥↓</kbd>
                    </button>
                    <button className="ws-menu-item" onClick={() => { setMenuFor(null); duplicateBlock(block.id) }}>
                      <Copy className="w-3.5 h-3.5" /> Duplicate
                    </button>
                    <button className="ws-menu-item text-loss-600 hover:bg-loss-50" onClick={() => { setMenuFor(null); removeBlock(block.id) }}>
                      <Trash2 className="w-3.5 h-3.5" /> Delete block
                    </button>
                  </div>
                </>
              )}

              {/* Block body */}
              <div className="flex-1 min-w-0 relative">
                {isText && (
                  <div className={cn('flex items-start gap-2', block.type === 'quote' && 'border-l-[3px] border-ink-200 pl-4')}>
                    {block.type === 'bullet' && <span className="select-none text-ink-400 leading-7 text-[15px]">•</span>}
                    {block.type === 'numbered' && (
                      <span className="select-none text-ink-400 leading-7 text-sm tabular-nums min-w-[1.2em] text-right">{numbers[block.id]}.</span>
                    )}
                    {block.type === 'todo' && (
                      <button
                        onClick={() => { block.checked = !block.checked; onStructure() }}
                        className={cn(
                          'mt-[7px] w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors',
                          block.checked ? 'bg-accent-500 border-accent-500 text-white' : 'border-ink-300 hover:border-accent-400',
                        )}
                      >
                        {block.checked && <svg viewBox="0 0 10 8" className="w-2.5 h-2.5 fill-none stroke-current stroke-2"><path d="M1 4l2.5 2.5L9 1" /></svg>}
                      </button>
                    )}
                    <div
                      ref={registerEl(block.id)}
                      data-block-id={block.id}
                      contentEditable
                      suppressContentEditableWarning
                      spellCheck={block.type !== 'code'}
                      data-placeholder={
                        blocks.length === 1 && i === 0 && block.type === 'p'
                          ? 'Write anything — “/” for blocks · select text to format…'
                          : block.type === 'todo' ? 'To-do' : ''
                      }
                      onKeyDown={(e) => handleKeyDown(e, block)}
                      onInput={(e) => handleInput(e, block)}
                      onPaste={(e) => handlePaste(e, block)}
                      className={cn(
                        'ws-block flex-1 min-w-0 outline-none whitespace-pre-wrap break-words py-0.5',
                        typeClass[block.type],
                        block.type === 'todo' && (block.checked ? 'line-through text-ink-400' : 'text-ink-800'),
                      )}
                    />
                  </div>
                )}

                {block.type === 'code' && (
                  <textarea
                    ref={registerEl(block.id)}
                    defaultValue={block.text || ''}
                    spellCheck={false}
                    rows={1}
                    onKeyDown={(e) => {
                      if (e.key === 'Tab') {
                        e.preventDefault()
                        const el = e.currentTarget
                        const s = el.selectionStart
                        el.setRangeText('  ', s, el.selectionEnd, 'end')
                        block.text = el.value
                        onText()
                      }
                      if (e.key === 'Escape') e.currentTarget.blur()
                      if (e.altKey && e.key === 'ArrowUp')   { e.preventDefault(); moveBlock(block.id, -1); return }
                      if (e.altKey && e.key === 'ArrowDown') { e.preventDefault(); moveBlock(block.id, +1); return }
                      // Leave the code block with plain arrows at the boundaries
                      const ta = e.currentTarget
                      if (e.key === 'ArrowDown' && ta.selectionEnd === ta.value.length) {
                        const next = blocks.slice(idx(block.id) + 1).find((b) => TEXT_TYPES.has(b.type))
                        if (next) {
                          e.preventDefault()
                          const nel = blockEls.current.get(next.id)
                          if (nel) { nel.focus(); setCaret(nel, 0) }
                        } else {
                          e.preventDefault()
                          const nb = newBlock('p')
                          insertBlockAfter(block.id, nb)
                          focusBlock(nb.id, 0)
                        }
                      }
                      if (e.key === 'ArrowUp' && ta.selectionStart === 0) {
                        const prev = [...blocks.slice(0, idx(block.id))].reverse().find((b) => TEXT_TYPES.has(b.type))
                        if (prev) {
                          e.preventDefault()
                          const pel = blockEls.current.get(prev.id)
                          if (pel) { pel.focus(); setCaret(pel, (prev.text || '').length) }
                        }
                      }
                    }}
                    onInput={(e) => {
                      block.text = e.currentTarget.value
                      e.currentTarget.style.height = 'auto'
                      e.currentTarget.style.height = `${e.currentTarget.scrollHeight}px`
                      onText()
                    }}
                    className="w-full resize-none overflow-hidden rounded-xl bg-ink-50 border border-ink-100 px-4 py-3 font-mono text-[13px] leading-6 text-ink-800 outline-none focus:border-accent-300 focus:ring-2 focus:ring-accent-100 my-1"
                    placeholder="Code…"
                  />
                )}

                {block.type === 'divider' && (
                  <div className="py-2.5"><hr className="border-ink-150" /></div>
                )}

                {block.type === 'table' && (
                  <TableBlock block={block} onChange={onText} />
                )}

                {block.type === 'attachment' && (
                  <AttachmentBlock
                    noteId={note.id}
                    attachment={(note.attachments || []).find((a) => a.id === block.attId)}
                  />
                )}

                {/* Slash menu */}
                {slash?.blockId === block.id && (
                  <div className="absolute left-0 top-full z-40 mt-1 w-72 rounded-xl bg-white border border-ink-100 shadow-elev-3 py-1.5 animate-scale-in max-h-80 overflow-y-auto">
                    {filteredSlashItems(slash.query).map((it, n) => (
                      <button
                        key={it.key}
                        onMouseDown={(e) => { e.preventDefault(); applySlash(block, it.key) }}
                        onMouseEnter={() => setSlash((s) => ({ ...s, idx: n }))}
                        className={cn(
                          'w-full flex items-center gap-3 px-3 py-2 text-left',
                          n === slash.idx ? 'bg-accent-50' : 'hover:bg-ink-50',
                        )}
                      >
                        <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-ink-50 border border-ink-100 shrink-0">
                          <it.icon className="w-4 h-4 text-ink-600" />
                        </span>
                        <span className="min-w-0">
                          <span className="block text-sm font-medium text-ink-800">{it.label}</span>
                          <span className="block text-[11px] text-ink-400 truncate">{it.hint}</span>
                        </span>
                      </button>
                    ))}
                    {filteredSlashItems(slash.query).length === 0 && (
                      <div className="px-3 py-2 text-sm text-ink-400">No matching block</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {/* Click below the last block → append a paragraph */}
      <div
        className="h-24 -mt-24 cursor-text"
        onClick={() => {
          const last = blocks[blocks.length - 1]
          if (last && TEXT_TYPES.has(last.type) && !(last.text || '').length) {
            const el = blockEls.current.get(last.id)
            if (el) { el.focus(); return }
          }
          const nb = newBlock('p')
          blocks.push(nb)
          focusBlock(nb.id, 0)
        }}
      />
    </div>
  )
}
